import { find, pipe } from 'remeda'
import { debounceLiveViewPush, type ViewHook } from '../helpers/utils'

type SplitLength = Readonly<{
  unit: 'percent' | 'pixels'
  value: number
}>

type SplitRepositionPayload = Readonly<{
  id: string
  position: number
}>

type SplitHookContext = ViewHook & {
  _splitController?: SplitController
}

type SplitController = Readonly<{
  destroy: () => void
  disconnected: () => void
  reconnected: () => void
}>

type SplitPanelElement = HTMLElement & {
  orientation: 'horizontal' | 'vertical'
  position: number
  positionInPixels: number
}

const SPLIT_LENGTH_PATTERN = /^(-?(?:\d+(?:\.\d+)?|\.\d+))(px|%)?$/

const isSplitPanel = (element: HTMLElement): element is SplitPanelElement =>
  element.tagName === 'WA-SPLIT-PANEL'

export const parseSplitLength = (
  value: string | undefined
): SplitLength | undefined => {
  const match = value?.trim().match(SPLIT_LENGTH_PATTERN)

  if (!match) {
    return undefined
  }

  const parsed = Number(match[1])

  if (!Number.isFinite(parsed)) {
    return undefined
  }

  return {
    unit: match[2] === '%' ? 'percent' : 'pixels',
    value: parsed
  }
}

const lengthInPixels = (length: SplitLength, total: number): number =>
  length.unit === 'percent' ? (length.value / 100) * total : length.value

export const splitAxisSize = (panel: SplitPanelElement): number => {
  const { height, width } = panel.getBoundingClientRect()

  return panel.orientation === 'vertical' ? height : width
}

const constraintInPixels = (
  value: string | undefined,
  total: number,
  fallback: number
): number => {
  const length = parseSplitLength(value)

  return length ? lengthInPixels(length, total) : fallback
}

const clampPosition = (position: number, min: number, max: number): number => {
  if (position <= min) {
    return min
  }

  if (position >= max) {
    return max
  }

  return position
}

const constrainPixels = (
  panel: SplitPanelElement,
  position: number
): number => {
  const total = splitAxisSize(panel)
  const min = constraintInPixels(panel.dataset.min, total, 0)
  const max = constraintInPixels(panel.dataset.max, total, total)

  return clampPosition(position, min, max)
}

export const splitPositionInPixels = (panel: SplitPanelElement): number =>
  constrainPixels(panel, (panel.position / 100) * splitAxisSize(panel))

const positionsMatch = (left: number, right: number): boolean =>
  Math.round(left) === Math.round(right)

const setupOrientationRemeasure = (panel: SplitPanelElement): (() => void) => {
  let restoreFrame: number | undefined
  let restorePadding: (() => void) | undefined

  const restore = (): void => {
    if (restoreFrame !== undefined) {
      window.cancelAnimationFrame(restoreFrame)
      restoreFrame = undefined
    }

    restorePadding?.()
    restorePadding = undefined
  }

  const remeasure = (): void => {
    restore()

    const property = 'padding-inline-end'
    const inlineValue = panel.style.getPropertyValue(property)
    const inlinePriority = panel.style.getPropertyPriority(property)
    const computedValue = Number.parseFloat(
      window.getComputedStyle(panel).paddingInlineEnd
    )
    const padding = Number.isFinite(computedValue) ? computedValue : 0

    panel.style.setProperty(property, `${padding + 1}px`)
    restorePadding = () => {
      if (inlineValue) {
        panel.style.setProperty(property, inlineValue, inlinePriority)
      } else {
        panel.style.removeProperty(property)
      }
    }

    // Pro 3.10 remeasures its drag axis from resize observations.
    restoreFrame = window.requestAnimationFrame(() => {
      restoreFrame = window.requestAnimationFrame(() => restore())
    })
  }

  const observer = new MutationObserver((mutations) => {
    const orientationChange = pipe(
      mutations,
      find(({ attributeName }) => attributeName === 'orientation')
    )

    if (orientationChange) {
      remeasure()
    }
  })

  observer.observe(panel, {
    attributeFilter: ['orientation'],
    attributes: true
  })

  return () => {
    observer.disconnect()
    restore()
  }
}

const setupSplit = (hook: SplitHookContext): SplitController => {
  if (!isSplitPanel(hook.el)) {
    return {
      destroy: () => undefined,
      disconnected: () => undefined,
      reconnected: () => undefined
    }
  }

  const panel = hook.el
  const cleanupOrientationRemeasure = setupOrientationRemeasure(panel)
  const eventName = panel.dataset.eventOnReposition
  const target = panel.getAttribute('phx-target') ?? undefined
  const isInPixels = panel.hasAttribute('position-in-pixels')
  const debouncedReposition = eventName
    ? debounceLiveViewPush(hook, eventName, {
        waitMs: 300,
        timing: 'trailing',
        target,
        errorMessage: 'Split panel reposition failed'
      })
    : undefined
  let lastPosition =
    isInPixels && Number.isFinite(panel.positionInPixels)
      ? constrainPixels(panel, panel.positionInPixels)
      : isInPixels
        ? splitPositionInPixels(panel)
        : panel.position
  let pendingPosition: number | undefined
  let queuedPosition: number | undefined
  let replayQueuedPosition = false
  let isDisconnected = false
  let pushSequence = 0

  const pushPosition = (position: number, force = false): void => {
    if (isDisconnected) {
      queuedPosition = position
      replayQueuedPosition = true
      return
    }

    if (
      (!force &&
        pendingPosition === undefined &&
        positionsMatch(position, lastPosition)) ||
      (pendingPosition !== undefined &&
        positionsMatch(position, pendingPosition))
    ) {
      return
    }

    pushSequence += 1
    const sequence = pushSequence
    const payload: SplitRepositionPayload = { id: panel.id, position }

    pendingPosition = position
    debouncedReposition
      ?.call(payload)
      .then(() => {
        if (sequence === pushSequence) {
          lastPosition = position
          pendingPosition = undefined
        }
      })
      .catch(() => {
        if (sequence === pushSequence) {
          pendingPosition = undefined
        }
      })
  }

  const onReposition = (event: Event): void => {
    if (event.target !== panel || !debouncedReposition) {
      return
    }

    const position = isInPixels ? splitPositionInPixels(panel) : panel.position

    if (!Number.isFinite(position)) {
      return
    }

    pushPosition(position)
  }

  const onToggle = (event: Event): void => {
    if (event.target !== panel) {
      return
    }

    const defaultPosition = parseSplitLength(panel.dataset.default)

    if (!defaultPosition) {
      return
    }

    const total = splitAxisSize(panel)
    const min = constraintInPixels(panel.dataset.min, total, 0)
    const current = splitPositionInPixels(panel)
    const expanded = lengthInPixels(defaultPosition, total)

    panel.positionInPixels = current <= min + 5 ? expanded : min
  }

  if (debouncedReposition) {
    panel.addEventListener('wa-reposition', onReposition)
  }
  panel.addEventListener('split:toggle', onToggle)

  return {
    disconnected: () => {
      isDisconnected = true

      if (pendingPosition !== undefined) {
        queuedPosition = pendingPosition
        replayQueuedPosition = true
      }

      pushSequence += 1
      pendingPosition = undefined
      debouncedReposition?.cancel()
    },
    reconnected: () => {
      isDisconnected = false

      if (queuedPosition === undefined) {
        return
      }

      const position = queuedPosition
      const force = replayQueuedPosition

      queuedPosition = undefined
      replayQueuedPosition = false
      pushPosition(position, force)
    },
    destroy: () => {
      panel.removeEventListener('wa-reposition', onReposition)
      panel.removeEventListener('split:toggle', onToggle)
      cleanupOrientationRemeasure()
      pushSequence += 1
      pendingPosition = undefined
      queuedPosition = undefined
      replayQueuedPosition = false
      debouncedReposition?.cancel()
    }
  }
}

export const FacadeSplit = {
  mounted(this: SplitHookContext): void {
    this._splitController = setupSplit(this)
  },

  disconnected(this: SplitHookContext): void {
    this._splitController?.disconnected()
  },

  reconnected(this: SplitHookContext): void {
    this._splitController?.reconnected()
  },

  destroyed(this: SplitHookContext): void {
    this._splitController?.destroy()
    this._splitController = undefined
  }
}
